import { Badge, Box, Flex, Heading, Text } from '@radix-ui/themes';
import { CommonBackArrow } from 'components/common/buttons/back-arrow';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonSelectInput } from 'components/common/form/select';
import { PitchPriorityBadge } from 'components/common/pitch-priority-badge';
import { PitcherHandBadge } from 'components/common/pitcher-hand-badge';
import { CommonTable } from 'components/common/table';
import { TableProvider } from 'components/common/table/context';
import usePitchPerformanceStore from 'components/sections/admin-portal/machines/dialogs/pitch-performance/store/use-pitch-performance-store';
import env from 'config';
import {
  CheckedContext,
  CheckedProvider,
} from 'contexts/layout/checked.context';
import { max, min, parseISO } from 'date-fns';
import { format } from 'date-fns-tz';
import {
  MachineSettingsQueryParams,
  MachineSettingsSection,
} from 'enums/machine-settings';
import { TABLES } from 'enums/tables';
import { PaginationID } from 'interfaces/cookies/i-app.cookie';
import { IOptionExt } from 'interfaces/forms/option-ext';
import { ITableCheckable } from 'interfaces/tables/checking';
import { ITypedTableColumn } from 'interfaces/tables/columns';
import { ITablePageable } from 'interfaces/tables/pagination';
import { ITableSortable } from 'interfaces/tables/sorting';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { BallHelper } from 'lib_ts/classes/ball.helper';
import { MachinePerformanceHelper } from 'lib_ts/classes/machine-performance.helper';
import { FT_TO_INCHES } from 'lib_ts/classes/math.utilities';
import { LOCAL_TIMEZONE } from 'lib_ts/enums/datetime.enums';
import { BallType } from 'lib_ts/enums/machine.enums';
import {
  MAX_BREAKS_DELTA_IN,
  MAX_BREAKS_DELTA_IN_TRAJEKT_36,
  MAX_PLATE_DELTA_IN,
  MAX_VELO_DELTA_MPH,
} from 'lib_ts/enums/pitch-performance';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IFireWithMetrics } from 'lib_ts/interfaces/data/i-pitch-performance';
import { compact, first, isNil, sortBy } from 'lodash';
import { useContext, useEffect, useMemo } from 'react';
import { paths } from 'routes/paths';

const PAGE_SIZES = TABLES.PAGE_SIZES.MD;

interface IHoCProps {
  onDrillUp: () => void;
}

// MARK: HoC
// ? wrap in an additional CheckedProvider and TableProvider so it can maintain a separate checked/table state than the PitchPerfGroupTable
export const PitchPerfShotTableHoC = ({ onDrillUp }: IHoCProps) => {
  // in this context, it's all the groups that share the same pitch (bs_hash)
  const visibleGroups = usePitchPerformanceStore((s) => s.visibleGroups);

  // aka: shots from the visible groups
  const visibleMetrics = useMemo(
    () =>
      visibleGroups.flatMap((g) =>
        MachinePerformanceHelper.getFireGroupMetrics(g)
      ),
    [visibleGroups]
  );

  return (
    <TableProvider identifier="PitchPerfShotTableProvider">
      <CheckedProvider data={visibleMetrics}>
        <PPShotTable
          bsHash={first(visibleGroups)?.pitch.bs_hash ?? ''}
          metrics={visibleMetrics}
          onDrillUp={onDrillUp}
        />
      </CheckedProvider>
    </TableProvider>
  );
};

interface IProps extends IHoCProps {
  bsHash: string;
  metrics: IFireWithMetrics[];

  onDrillUp: () => void;
}

// MARK: component
const PPShotTable = ({ bsHash, metrics, onDrillUp }: IProps) => {
  const { tally, resetChecked, getChecked } = useContext(CheckedContext);

  const loading = usePitchPerformanceStore((s) => s.loading);
  // used for populating the ms hash dropdown so that selecting something won't remove other options
  const groups = usePitchPerformanceStore((s) => s.groups);
  const visibleGroups = usePitchPerformanceStore((s) => s.visibleGroups);
  const filterBallType = usePitchPerformanceStore((s) => s.filters.ball_type);
  const selectedMsHash = usePitchPerformanceStore((s) => s.selectedMsHash);

  const filteredMetrics = useMemo(() => {
    if (!selectedMsHash) {
      return metrics;
    }

    return metrics.filter(
      (m) => m.shot.target_ms.matching_hash === selectedMsHash
    );
  }, [metrics, selectedMsHash]);

  useEffect(() => {
    // keep selectedShotIds in sync with the checked items
    // we'll use selectedShotIds to show metrics/plots specific to the selected shots
    if (tally.checked > 0) {
      const selectedShots = metrics.filter((s) => getChecked(s.shot._id));
      usePitchPerformanceStore.setState({
        selectedShotIds: selectedShots.map((s) => s.shot._id),
      });
      return;
    }

    usePitchPerformanceStore.setState({ selectedShotIds: [] });
  }, [tally.key, tally.checked, metrics]);

  const pagination: ITablePageable = {
    paginationID: PaginationID.PitchPerformanceShotList,
    total: metrics.length,
    enablePagination: true,
    pageSizes: PAGE_SIZES,
  };

  const sort: ITableSortable = {
    enableSort: true,
    defaultSort: {
      key: '_created',
      dir: 1,
    },
  };

  const checkable: ITableCheckable = {
    checkboxColumnIndex: 0,
    // just a placeholder
    checkedActions: () => [],
  };

  // MARK: columns
  const columns = useMemo(() => {
    const maxBreaksDeltaIn =
      filterBallType === BallType.Trajekt_36
        ? MAX_BREAKS_DELTA_IN_TRAJEKT_36
        : MAX_BREAKS_DELTA_IN;

    // for comparing shots to the relevant group
    const groupsDict = ArrayHelper.firstByFn(
      visibleGroups,
      (g) => g.target.ms.matching_hash
    );

    const output: ITypedTableColumn<IFireWithMetrics>[] = [
      {
        label: 'Fired Date',
        key: '_created',
        sortRowsFn: (a, b, dir) =>
          (a.shot._created > b.shot._created ? -1 : 1) * dir,
        formatFn: (m) => (
          <Flex
            direction="column"
            gap="1"
            className="cursor-pointer"
            onClick={() => {
              const params = new URLSearchParams({
                [MachineSettingsQueryParams.MachineID]: m.shot.machineID,
                [MachineSettingsQueryParams.SectionID]:
                  MachineSettingsSection.ShotHistory,
                [MachineSettingsQueryParams.ShotID]: m.shot._id,
              });

              window.open(
                `${env.self_url}${paths.admin.machines}?${params.toString()}`,
                '_blank'
              );
            }}
          >
            <Text>
              {format(parseISO(m.shot._created), 'MM/dd/yyyy HH:mm z', {
                timeZone: LOCAL_TIMEZONE,
              })}
            </Text>
            <Box>
              <Badge color={RADIX.COLOR.NEUTRAL}>
                {m.shot.training ? 'Training' : 'BP'}
              </Badge>
            </Box>
          </Flex>
        ),
      },
      {
        label: 'common.speed',
        subLabel: 'mph',
        key: '_speed',
        sortRowsFn: (a, b, dir) => (a.speedMPH > b.speedMPH ? 1 : -1) * dir,
        formatFn: (m) => (
          <Flex direction="column" pt="2" pb="2">
            <Box>
              <Text>{m.speedMPH.toFixed(1)}</Text>
            </Box>
            <Box>
              <Text size="1" color={RADIX.COLOR.SECONDARY}>
                Target: {m.target.speedMPH.toFixed(1)}
              </Text>
            </Box>
            <Box>
              <Badge
                color={
                  Math.abs(m.delta.speedMPH) > MAX_VELO_DELTA_MPH
                    ? RADIX.COLOR.DANGER
                    : RADIX.COLOR.SUCCESS
                }
              >
                {m.delta.speedMPH > 0 && '+'}
                {m.delta.speedMPH.toFixed(1)}
              </Badge>
            </Box>
          </Flex>
        ),
      },
      {
        label: 'common.v-break',
        subLabel: 'in',
        key: '_breakZ',
        sortRowsFn: (a, b, dir) =>
          (a.break.zInches > b.break.zInches ? 1 : -1) * dir,
        formatFn: (m) => {
          // calculate delta between shot and group target
          const group =
            groupsDict[m.shot.target_ms.matching_hash ?? 'no-matching-hash'];

          const delta = group
            ? m.break.zInches - group.target.break.zInches
            : undefined;

          // todo: use delta from metrics if the targets align
          if (group) {
            console.assert(
              group.target.break.zInches === m.target.break.zInches,
              'Target breaks zInches mismatch between fire group and metrics'
            );
          }

          return (
            <Flex direction="column" pt="2" pb="2">
              <Box>
                <Text>{m.break.zInches.toFixed(1)}</Text>
              </Box>
              <Box>
                <Text size="1" color={RADIX.COLOR.SECONDARY}>
                  Target:{' '}
                  {/* Show the Pitch/Fire Group's target, not the individual shot's target in case PY changed between pitch design and shot */}
                  {group?.target.break.zInches.toFixed(1) ?? 'N/A'}
                </Text>
              </Box>
              {!isNil(delta) && (
                <Box>
                  <Badge
                    color={
                      Math.abs(delta) > maxBreaksDeltaIn
                        ? RADIX.COLOR.DANGER
                        : RADIX.COLOR.SUCCESS
                    }
                  >
                    {delta > 0 && '+'}
                    {delta.toFixed(1)}
                  </Badge>
                </Box>
              )}
            </Flex>
          );
        },
      },
      {
        label: 'common.h-break',
        subLabel: 'in',
        key: '_breakX',
        sortRowsFn: (a, b, dir) =>
          (a.break.xInches > b.break.xInches ? 1 : -1) * dir,
        formatFn: (m) => {
          // calculate delta between shot and group target
          const group =
            groupsDict[m.shot.target_ms.matching_hash ?? 'no-matching-hash'];

          const delta = group
            ? m.break.xInches - group.target.break.xInches
            : undefined;

          // todo: use delta from metrics if the targets align
          if (group) {
            console.assert(
              group.target.break.xInches === m.target.break.xInches,
              'Target breaks xInches mismatch between fire group and metrics'
            );
          }

          return (
            <Flex direction="column" pt="2" pb="2">
              <Box>
                <Text>{-m.break.xInches.toFixed(1)}</Text>
              </Box>
              <Box>
                <Text size="1" color={RADIX.COLOR.SECONDARY}>
                  Target:{' '}
                  {/* Show the Pitch/Fire Group's target, not the individual shot's target in case PY changed between pitch design and shot */}
                  {group ? -group.target.break.xInches.toFixed(1) : 'N/A'}
                </Text>
              </Box>
              {!isNil(delta) && (
                <Box>
                  <Badge
                    color={
                      Math.abs(delta) > maxBreaksDeltaIn
                        ? RADIX.COLOR.DANGER
                        : RADIX.COLOR.SUCCESS
                    }
                  >
                    {-delta.toFixed(1)}
                    {-delta > 0 && '+'}
                  </Badge>
                </Box>
              )}
            </Flex>
          );
        },
      },
      {
        label: 'common.plate-z',
        subLabel: 'in',
        key: '_plateZ',
        sortRowsFn: (a, b, dir) =>
          (a.plate.plate_z > b.plate.plate_z ? 1 : -1) * dir,
        formatFn: (m) => (
          <Flex direction="column" pt="2" pb="2">
            <Box>
              <Text>{(m.plate.plate_z * FT_TO_INCHES).toFixed(1)}</Text>
            </Box>
            <Box>
              <Text size="1" color={RADIX.COLOR.SECONDARY}>
                Target: {(m.target.plate.plate_z * FT_TO_INCHES).toFixed(1)}
              </Text>
            </Box>
            <Box>
              <Badge
                color={
                  Math.abs(m.delta.plate.plate_z * FT_TO_INCHES) >
                  MAX_PLATE_DELTA_IN
                    ? RADIX.COLOR.DANGER
                    : RADIX.COLOR.SUCCESS
                }
              >
                {m.delta.plate.plate_z > 0 && '+'}
                {(m.delta.plate.plate_z * FT_TO_INCHES).toFixed(1)}
              </Badge>
            </Box>
          </Flex>
        ),
      },
      {
        label: 'common.plate-x',
        subLabel: 'in',
        key: '_plateX',
        sortRowsFn: (a, b, dir) =>
          (a.plate.plate_x > b.plate.plate_x ? 1 : -1) * dir,
        formatFn: (m) => (
          <Flex direction="column" pt="2" pb="2">
            <Box>
              <Text>{(m.plate.plate_x * FT_TO_INCHES).toFixed(1)}</Text>
            </Box>
            <Box>
              <Text size="1" color={RADIX.COLOR.SECONDARY}>
                Target: {(m.target.plate.plate_x * FT_TO_INCHES).toFixed(1)}
              </Text>
            </Box>
            <Box>
              <Badge
                color={
                  Math.abs(m.delta.plate.plate_x * FT_TO_INCHES) >
                  MAX_PLATE_DELTA_IN
                    ? RADIX.COLOR.DANGER
                    : RADIX.COLOR.SUCCESS
                }
              >
                {m.delta.plate.plate_x > 0 && '+'}
                {(m.delta.plate.plate_x * FT_TO_INCHES).toFixed(1)}
              </Badge>
            </Box>
          </Flex>
        ),
      },
    ];

    return output;
  }, [visibleGroups, filterBallType]);

  const drillUp = () => {
    // reset this table's internal checked state
    resetChecked();

    onDrillUp();

    usePitchPerformanceStore.setState({
      selectedPitchId: '',
      selectedMsHash: '',
      selectedShotIds: [],
    });
  };

  // prefer the current MS group, otherwise use the first group found
  const sampleGroup =
    visibleGroups.find((m) => m.isCurrentMs) ?? first(visibleGroups);

  const msHashOptions: IOptionExt[] = useMemo(() => {
    // each group contains shots w/ the same matching_hash in the target ms
    const output: IOptionExt[] = compact(
      groups.map((g) => {
        if (g.pitch.bs_hash !== bsHash) {
          // group belongs to a different pitch => skip
          return undefined;
        }

        if (!g.target.ms.matching_hash) {
          // group has no matching_hash => skip
          return undefined;
        }

        const shotDates = g.fires.map((s) => parseISO(s.shot._created));
        const minDate = format(min(shotDates), 'MMM d, yyyy HH:mm:ss', {
          timeZone: LOCAL_TIMEZONE,
        });
        const maxDate = format(max(shotDates), 'MMM d, yyyy HH:mm:ss', {
          timeZone: LOCAL_TIMEZONE,
        });

        const o: IOptionExt = {
          group: g.isCurrentMs ? 'Current' : 'Previous',
          // truncate the hash to 4 characters
          label: `MS #${g.target.ms.matching_hash.slice(-4)}`,
          // start and end dates of the group
          description: `${minDate} - ${maxDate}`,
          // current should sort to the end, previous should sort by ascending last fired
          // reverse after to get current at the top, then descending last fired
          sortKey: `${g.isCurrentMs ? '99' : '00'}_${g.lastFired ?? 'unknown'}`,
          suffix: (
            <Flex gap="1">
              <Badge color={RADIX.COLOR.SECONDARY}>
                {g.fires.length} shots
              </Badge>
              {g.isCurrentMs ? (
                <Badge color={RADIX.COLOR.SUCCESS}>Current</Badge>
              ) : undefined}{' '}
            </Flex>
          ),
          value: g.target.ms.matching_hash,
        };

        return o;
      })
    );

    // skip sort on the input so that the order of the options is preserved (newest first)
    return sortBy(output, 'sortKey').reverse();
  }, [groups, bsHash]);

  // MARK: render
  return (
    <ErrorBoundary identifier="PPShot">
      <Box>
        <CommonTable
          identifier="PPShotTable"
          dataTestID="PPShot"
          toolbarContent={
            <Flex align="center" justify="between" gap="2">
              <Box>
                <Flex align="center" gap="2" mb="1">
                  <CommonBackArrow onClick={drillUp} />

                  <Heading size="4">{sampleGroup?.pitch?.name}</Heading>
                  <Flex gap="1">
                    {sampleGroup?.target && (
                      <PitcherHandBadge
                        value={BallHelper.getPitcherHand(
                          sampleGroup.target.traj.px
                        )}
                      />
                    )}

                    {sampleGroup?.pitch && (
                      <PitchPriorityBadge value={sampleGroup.pitch.priority} />
                    )}

                    <Badge color={RADIX.COLOR.SECONDARY}>
                      {metrics.length} Shots
                    </Badge>
                  </Flex>
                </Flex>
                <Text color={RADIX.COLOR.SECONDARY}>
                  Details for each shot of the selected pitch
                </Text>
              </Box>

              <Box>
                <CommonSelectInput
                  id="filter-ms-hash"
                  placeholder="All MS"
                  value={selectedMsHash ?? ''}
                  onChange={(value) =>
                    usePitchPerformanceStore.setState({
                      selectedMsHash: value,
                    })
                  }
                  options={msHashOptions}
                  skipSort
                  optional
                />
              </Box>
            </Flex>
          }
          displayColumns={columns}
          displayData={filteredMetrics}
          loading={loading}
          scrollHeight="400px"
          {...pagination}
          {...checkable}
          {...sort}
        />
      </Box>
    </ErrorBoundary>
  );
};
